import Box from "@/common/components/Box";
import { ErrorMessageWrapper } from "@/common/components/ErrorMessageWrapper";
import GoBackButton from "@/common/components/GoBackButton";
import Table from "@/common/components/Table";
import { lineItemsDefaultColumns } from "@/common/constants/lineItemsDefaultColumns.constant";
import { EventLineItemsTransformer } from "@/common/services/eventLineItemsTransformer";
import { formatCurrency, formatDate } from "@/common/utils/formatters";
import ConfirmFiscalDocument from "@/features/billing/components/EventsWithFiscalDocumentRequirement/ConfirmFiscalDocument";
import EventCardDetails from "@/features/billing/components/EventsWithFiscalDocumentRequirement/EventCardDetails";
import EventResume from "@/features/billing/components/EventsWithFiscalDocumentRequirement/EventResume";
import EventWithFiscalDocumentRequirementLoading from "@/features/billing/components/EventsWithFiscalDocumentRequirement/EventWithFiscalDocumentRequirementLoading";
import FiscalDocumentRejected from "@/features/billing/components/EventsWithFiscalDocumentRequirement/FiscalDocumentRejected";
import RejectFiscalDocument from "@/features/billing/components/EventsWithFiscalDocumentRequirement/RejectFiscalDocument";
import RemoveFiscalDocument from "@/features/billing/components/EventsWithFiscalDocumentRequirement/RemoveFiscalDocument";
import UploadFiscalDocument from "@/features/billing/components/EventsWithFiscalDocumentRequirement/UploadFiscalDocument";
import InssuanceData from "@/features/billing/components/InssuanceData";
import { fiscalDocumentFileClassName } from "@/features/billing/constants/fiscalDocumentFileClassName.constant";
import useBillingEvent from "@/features/billing/hooks/useBillingEvent";
import useCompanyBranchWithFiscalDocumentIssuerData from "@/features/billing/hooks/useCompanyBranchWithFiscalDocumentIssuerData";
import { LineItemData } from "@/features/consumption/types/consumption.type";
import { DownloadIcon, InfoIcon } from "lucide-react";
import { useParams } from "react-router-dom";
import { twMerge } from "tailwind-merge";

const BillingEventsWithFiscalDocumentRequirementShow = () => {
  const { id } = useParams();

  const {
    data: event,
    isPending: eventIsPending,
    error: eventError = {} as any,
    refetch: refetchEvent,
  } = useBillingEvent({ id });

  const { data: fiscalDocumentIssuerData } = useCompanyBranchWithFiscalDocumentIssuerData({ id: event?.eventable?.client_branch.id });

  const fiscalDocumentStatus = event?.eventable?.fiscal_document_status;
  const hasFiscalDocument = !!event?.eventable?.fiscal_document
  const fiscalDocumentIsConfirmed = fiscalDocumentStatus === 'confirmed'

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center justify-between">
        <div className="flex gap-2 items-center">
          <GoBackButton
            fallback='/events-with-fiscal-document-requirement'
            searchLastRouteFromFallback
          />

          <h2 className="text-2xl font-bold">Transação {event?.protocol_number}</h2>
        </div>
        <div className="flex gap-3 items-end">
          {event && (fiscalDocumentStatus === 'pending' || fiscalDocumentStatus === 'rejected') && (
            <div className="flex items-center gap-3">
              <UploadFiscalDocument eventId={event.id} />
            </div>
          )}

          {event && fiscalDocumentStatus === 'uploaded' && (
            <div className="flex items-center gap-3">
              <RejectFiscalDocument eventId={event.id} />

              <ConfirmFiscalDocument eventId={event.id} />
            </div>
          )}

          {event && hasFiscalDocument && !fiscalDocumentIsConfirmed && (
            <RemoveFiscalDocument eventId={event.id} />
          )}
        </div>
      </div>

      {eventIsPending && <EventWithFiscalDocumentRequirementLoading />}

      {!eventIsPending && (
        <ErrorMessageWrapper
          className="h-[calc((100vh-88px-2rem-1.5rem))]"
          statusCode={eventError?.status}
          refetchFunction={refetchEvent}
          notFoundButtonText="Ir para Recolha de notas por transações"
        >
          <Box className="h-[calc((100vh-88px-2rem-1.5rem))] overflow-auto flex gap-6">
            <div className={`flex flex-col gap-6 h-max w-1/2`}>
              <EventResume event={event} />

              {event?.eventable?.card && <EventCardDetails card={event.eventable.card} />}

              <div>
                <Table
                  className="max-h-[240px]"
                  columns={lineItemsDefaultColumns}
                  rows={EventLineItemsTransformer.tableTransformer()((event?.eventable?.line_items || []) as unknown as LineItemData[]) || []}
                  isLoadingData={eventIsPending}
                  hasPagination={false}
                  loadingRows={1}
                  page={1}
                  totalPages={1}
                  bottomContent={(
                    <div className="flex flex-col gap-1 w-full">
                      <div className="flex justify-between w-full">
                        <p className="text-xs text-white-400">Total em tarifas</p>
                        <p className="text-xs text-white-400">{formatCurrency(Number(event?.fee_total) || 0)}</p>
                      </div>

                      <div className="flex justify-between w-full">
                        <p className="text-sm">Total bruto</p>
                        <p className="text-blue-800 text-sm">{formatCurrency(Number(event?.eventable?.total_amount) || 0)}</p>
                      </div>
                    </div>
                  )}
                />
              </div>
            </div>

            <div className="flex flex-col gap-6 w-1/2">
              <InssuanceData issuerData={fiscalDocumentIssuerData} />

              <div className="flex flex-col gap-3">
                {fiscalDocumentStatus === 'rejected' && <FiscalDocumentRejected fiscalDocument={event?.eventable?.fiscal_document} />}

                {event?.eventable?.fiscal_documents ? (
                  <div className="flex flex-col gap-4">
                    {event?.eventable?.fiscal_documents.map((document) => (
                      <div
                        className={twMerge(
                          "flex justify-between items-center rounded-md px-4 py-3 bg-white-200 text-sm",
                          event.eventable?.fiscal_document_status && fiscalDocumentFileClassName[event.eventable?.fiscal_document_status as keyof typeof fiscalDocumentFileClassName]
                        )}
                        data-testid='fiscal_document_event_file_area'
                      >
                        <div className="flex flex-col gap-1">
                          <span className="text-white-500 text-sm italic">Nota fiscal</span>
                          <div className="flex items-center gap-8">
                            <div>
                              <span className="text-xs">Data de criação:</span>
                              <p className="text-white-900">{document.issued_at ? formatDate(new Date(document.issued_at)) : "--"}</p>
                            </div>
                            <div>
                              <span className="text-xs">Data de inclusão no sistema:</span>
                              <p className="text-white-900">{document.created_at ? formatDate(new Date(document.created_at)) : "--"}</p>
                            </div>
                          </div>
                        </div>

                        <div className="flex gap-6 items-center">
                          {document.xml?.url && (
                            <a href={document.xml?.url} download className="flex items-center gap-1 text-white-900 hover:text-blue-800 transition-colors">
                              <DownloadIcon /> XML
                            </a>
                          )}
                          {document.pdf?.url && (
                            <a href={document.pdf?.url} download className="flex items-center gap-1 text-white-900 hover:text-blue-800 transition-colors">
                              <DownloadIcon /> PDF
                            </a>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <InfoIcon className="w-[28px]" strokeWidth={1} color="var(--warning)" />
                    <div className="flex flex-col">
                      <p className="text-xs font-bold">O estabelecimento ainda não enviou essa nota.</p>
                      <p className="text-xs italic font-light">Caso o prazo termine, a transação será cancelada e sua empresa não será debitada.</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </Box>
        </ErrorMessageWrapper>
      )}
    </div>
  );
};

export default BillingEventsWithFiscalDocumentRequirementShow;
